import { Bookmark, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import type { BriefTemplate } from "../types";
import { useServiceText } from "../service-text";
import { useT } from "../i18n";

/**
 * Start from a brief you already wrote.
 *
 * Shown above the service picker, and only when there is something to show — an
 * empty "Saved briefs" heading teaches the reader that the feature is not for them
 * and they stop looking at that part of the screen.
 *
 * A template whose service has since been retired is listed but not offered. Hiding
 * it would leave the client wondering where their brief went; letting them click it
 * would open a form that cannot be submitted.
 */
export default function SavedBriefs() {
  const t = useT();
  const st = useServiceText();
  const [templates, setTemplates] = useState<BriefTemplate[] | null>(null);

  useEffect(() => {
    let live = true;
    dashboardApi.templates()
      // A picker that fails to load must not render as "you have no saved briefs".
      .then((data) => { if (live) setTemplates(data.templates); })
      .catch(() => { if (live) setTemplates(null); });
    return () => { live = false; };
  }, []);

  async function remove(template: BriefTemplate) {
    if (!window.confirm(t("briefs.confirmDelete", { name: template.name }))) return;
    try {
      await dashboardApi.deleteTemplate(template.id);
      setTemplates((current) => (current || []).filter((row) => row.id !== template.id));
      toast.success(`Deleted "${template.name}".`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.deleteBrief"));
    }
  }

  if (!templates || templates.length === 0) return null;

  return (
    <section className="saved-briefs">
      <div className="saved-briefs-head">
        <span className="section-eyebrow">{t("briefs.title")}</span>
        <h4>{t("briefs.intro2")}</h4>
      </div>
      <ul>
        {templates.map((template) => (
          <li key={template.id} className={template.serviceActive ? "" : "is-retired"}>
            {template.serviceActive ? (
              <Link href={`/request/new?template=${template.id}`} className="saved-brief-open">
                <Bookmark size={16} />
                <span>
                  <strong>{template.name}</strong>
                  <em>{st.name(template.serviceId, template.serviceName)}{template.quantity > 1 ? ` · ${template.quantity}` : ""}</em>
                </span>
              </Link>
            ) : (
              <span className="saved-brief-open" aria-disabled="true">
                <Bookmark size={16} />
                <span>
                  <strong>{template.name}</strong>
                  <em>{st.name(template.serviceId, template.serviceName)} is no longer offered</em>
                </span>
              </span>
            )}
            <button type="button" onClick={() => void remove(template)} aria-label={t("aria.deleteBrief", { name: template.name })}>
              <Trash2 size={15} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
